import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Button, Icon, Input, notification, Modal } from 'antd';
import cn from 'classnames';
import CommentOptions from '../../../../components/commentsChat/CommentOptions/CommentOptions';
import AttachedImages from '../../../../components/commentsChat/commentsFooter/attachedFiles/AttachedImages/AttachedImages';
import AttachedCommonFiles from '../../../../components/commentsChat/commentsFooter/attachedFiles/AttachedCommonFiles/AttachedCommonFiles';
import { FILE_SIZE_LIMIT } from '../../../../components/commentsChat/commentsFooter/CommentsChatFooter/constants';
import { CHAR_LIMIT } from './constants';
import CommentEditingBlock from './CommentEditingBlock';
import useSendEditedComment from '../../../../hooks/useSendEditedRequestComment';
import useSendComment from '../../../../hooks/useSendRequestComment';
import TextCounterWithHelp from './TextCounterWithHelp';
import styles from './Footer.module.scss';

const { TextArea } = Input;
const { confirm } = Modal;

const Footer = ({
  requestId,
  editedCommentData = null,
  setEditedCommentData,
  visible,
  updateData,
  showCheckboxOptions = false,
}) => {
  const attachFilesInput = useRef();

  const [comment, setComment] = useState('');
  const [attachedImages, setAttachedImages] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [availableForClient, setAvailableForClient] = useState(false);
  const [isSendLoading, setIsSendLoading] = useState(false);

  const sendEditedComment = useSendEditedComment(editedCommentData);
  const sendComment = useSendComment();

  const resetData = useCallback(() => {
    setComment('');
    setAvailableForClient(false);
    setAttachedImages([]);
    setAttachedFiles([]);
  }, []);

  useEffect(() => {
    if (!visible) {
      setEditedCommentData(null);
      resetData();
    }
  }, [visible, resetData, setEditedCommentData]);

  useEffect(() => {
    if (editedCommentData) {
      const { note, commentImages, commentFiles } = editedCommentData;
      const newNote = note === '(Вложение)' ? '' : note;
      setComment(newNote);
      setAttachedImages(commentImages);
      setAttachedFiles(commentFiles);
    } else {
      resetData();
    }
  }, [editedCommentData]);

  const handleTextAreaChange = (e) => {
    setComment(e.target.value);
  };

  const openErrorNotification = (description) => {
    notification['error']({
      message: 'Ошибка',
      description,
    });
  };

  const handleFileChange = (e) => {
    const attachedFilesArr = e.target.files;

    for (let file of attachedFilesArr) {
      if (file.size > FILE_SIZE_LIMIT) {
        openErrorNotification(
          'Размер прикрепляемого файла не должен превышать 15 MB.'
        );
        attachFilesInput.current.value = null;
        return;
      }

      if (file.type.includes('image')) {
        setAttachedImages((prev) => [...prev, file]);
      } else {
        setAttachedFiles((prev) => [...prev, file]);
      }
    }
    attachFilesInput.current.value = null;
  };

  const handleDeleteAttachedImage = useCallback(
    (index) => {
      const newAttachedImages = attachedImages.filter(
        (_image, imageIndex) => imageIndex !== index
      );

      setAttachedImages(newAttachedImages);
    },
    [attachedImages]
  );

  const handleDeleteAttachedFile = useCallback(
    (index) => {
      const newAttachedFiles = attachedFiles.filter(
        (_file, fileIndex) => fileIndex !== index
      );

      setAttachedFiles(newAttachedFiles);
    },
    [attachedFiles]
  );

  const handleSendComment = async () => {
    if (comment || attachedImages.length || attachedFiles.length) {
      setIsSendLoading(true);

      if (editedCommentData) {
        const data = {
          attachments: [...attachedImages, ...attachedFiles],
          comment,
        };

        await sendEditedComment(data);

        setEditedCommentData(null);
      } else {
        const data = {
          requestId,
          comment,
          availableForClient,
          attachments: [...attachedImages, ...attachedFiles],
        };

        await sendComment(data);
      }

      resetData();
      setIsSendLoading(false);
      updateData();
    }
  };

  const hasExceededLimit = comment?.length > CHAR_LIMIT;

  const isSendDisabled = useMemo(() => {
    return (
      !(comment?.trim() || attachedImages.length || attachedFiles.length) ||
      hasExceededLimit
    );
  }, [comment, attachedImages, attachedFiles, hasExceededLimit]);

  const confirmCancelEdit = (e) => {
    e.preventDefault();
    confirm({
      title: 'Вы уверены, что хотите прекратить редактирование?',
      onOk: () => setEditedCommentData(null),
      okText: 'Да',
      cancelText: 'Нет',
      onCancel: () => {},
    });
  };

  const messageKeyDown = useCallback(
    (e) => {
      if (!e.shiftKey && e.key === 'Enter') {
        e.preventDefault();
        handleSendComment();
      }
    },
    [handleSendComment]
  );

  return (
    <div
      className={styles.footer}
      onKeyDown={messageKeyDown}
    >
      {editedCommentData && (
        <CommentEditingBlock
          isSendLoading={isSendLoading}
          onConfirmCancelEdit={confirmCancelEdit}
        />
      )}
      {showCheckboxOptions && (
        <CommentOptions
          availableForClient={availableForClient}
          setAvailableForClient={setAvailableForClient}
        />
      )}

      <AttachedImages
        attachedImages={attachedImages}
        deleteAttachedImage={handleDeleteAttachedImage}
      />

      <div className={styles.inputSendBlock}>
        <div style={{ flexGrow: 1 }}>
          <div
            className={cn(styles.inputMessageBlock, {
              [styles.error]: hasExceededLimit,
            })}
          >
            <TextArea
              autoFocus
              value={comment}
              onChange={handleTextAreaChange}
              className={styles.textArea}
              placeholder="Сообщение"
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={isSendLoading}
            />
            <label className={styles.attachButton}>
              <input
                disabled={isSendLoading}
                multiple
                ref={attachFilesInput}
                className={styles.attachFilesInput}
                type="file"
                onChange={handleFileChange}
              />
              <Icon type="paper-clip" />
            </label>
          </div>
          <TextCounterWithHelp
            hasExceededLimit={hasExceededLimit}
            valueLength={comment?.length}
          />
        </div>

        <Button
          disabled={isSendDisabled}
          type="primary"
          loading={isSendLoading}
          shape="circle"
          icon="arrow-up"
          style={isSendDisabled ? { background: '#7aabff' } : null}
          className={styles.sendButton}
          onClick={handleSendComment}
        />
      </div>
      <AttachedCommonFiles
        attachedFiles={attachedFiles}
        deleteAttachedCommonFile={handleDeleteAttachedFile}
      />
    </div>
  );
};

export default memo(Footer);
